import { createAuthenticatedFetch } from "@assetwatch/auth";
import type {
  AppType,
  CreateMarketplaceRequestBody,
  CreateMarketplaceRequestResponse,
  MarketplaceCustomerOption,
  MarketplaceResponse,
} from "@assetwatch/marketplace-api";
import { hc } from "hono/client";
import { createMarketplaceId } from "../lib/requestId";

const configuredApiBaseUrl = (
  import.meta.env.VITE_MARKETPLACE_API_BASE_URL as string | undefined
)?.replace(/\/$/, "");
const configuredApiPathPrefix = (
  import.meta.env.VITE_MARKETPLACE_API_PATH_PREFIX as string | undefined
)?.trim();

const deployEnvironment =
  (import.meta.env.VITE_DEPLOY_ENVIRONMENT as string | undefined) ?? "dev";

function getDefaultApiBaseUrl(): string {
  if (deployEnvironment === "prod") {
    return "https://marketplace-api.assetwatch.com";
  }

  return `https://marketplace-api.${deployEnvironment}.assetwatch.com`;
}

const apiBaseUrl = configuredApiBaseUrl ?? getDefaultApiBaseUrl();
const apiPathPrefix =
  configuredApiPathPrefix === undefined
    ? "/marketplace"
    : configuredApiPathPrefix === ""
      ? ""
      : configuredApiPathPrefix.startsWith("/")
        ? configuredApiPathPrefix.replace(/\/$/, "")
        : `/${configuredApiPathPrefix.replace(/\/$/, "")}`;

const authenticatedFetch = createAuthenticatedFetch({
  createRequestId: () => createMarketplaceId("req"),
});

export const marketplaceClient = hc<AppType>(`${apiBaseUrl}${apiPathPrefix}`, {
  fetch: authenticatedFetch,
});

async function readResponse<T>(response: Response, operation: string) {
  if (!response.ok) {
    throw new Error(`${operation} request failed with ${response.status}`);
  }

  return response.json() as Promise<T>;
}

export async function fetchMarketplaceCustomers(): Promise<{
  customers: MarketplaceCustomerOption[];
}> {
  const response = await marketplaceClient.v1.marketplace.customers.$get();
  return readResponse(response, "Marketplace customer options");
}

export async function fetchMarketplace(
  externalCustomerId?: string,
): Promise<MarketplaceResponse> {
  const response = await marketplaceClient.v1.marketplace.$get({
    query: externalCustomerId ? { externalCustomerId } : {},
  });
  return readResponse(response, "Marketplace");
}

export async function createMarketplaceRequest(
  body: CreateMarketplaceRequestBody,
): Promise<CreateMarketplaceRequestResponse> {
  const response = await marketplaceClient.v1.marketplace.requests.$post({
    json: body,
  });
  return readResponse(response, "Marketplace request");
}
